import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Users, Calendar, Trophy, School, AlertTriangle, Send, TrendingUp, DollarSign, UserPlus, Search, Filter, X, Bell, MessageCircle, Mail, Clock, RefreshCw, Upload, FileText, Trash2, BarChart3, Award, Download, Cake, Gift } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api, { remindersAPI, whatsappAPI } from '../services/api';
import { toast } from 'sonner';

const AdminDashboardEnhanced = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState(null);
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [lowSessionsClients, setLowSessionsClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [programFilter, setProgramFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [coachFilter, setCoachFilter] = useState('all');
  
  const [assignment, setAssignment] = useState({
    coach_name: '',
    coach_email: '',
    schedule_days: '',
    schedule_times: '',
    start_date: '',
    program_details: ''
  });
  
  // New Coach Form
  const [newCoach, setNewCoach] = useState({
    full_name: '',
    email: '',
    phone: '',
    specialization: 'all',
    bio: '',
    certifications: '',
    create_login: true,
    password: 'coach123'
  });
  
  // New Client Form (Manual Registration)
  const [newClient, setNewClient] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    program_type: 'children',
    package_type: 'package',
    sessions_purchased: 12,
    amount_expected: 150000, // GHS 1500 in pesewas
    amount_paid: 0,
    discount_applied: 0,
    payment_status: 'pending',
    assigned_coach: 'none',
    assigned_schedule: '',
    start_date: ''
  });
  
  // Bulk Import state
  const [csvFile, setCsvFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);
  
  // Revenue Report state
  const [revenueReport, setRevenueReport] = useState(null);
  
  // Email Templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  const [message, setMessage] = useState({
    recipient_phone: '',
    recipient_email: '',
    message: '',
    send_via: 'email'
  });
  const [loading, setLoading] = useState(false);
  
  // Reminders state
  const [pendingReminders, setPendingReminders] = useState({ low_sessions: [], missed_sessions: [], inactive_7_days: [] });
  const [reminderHistory, setReminderHistory] = useState([]);
  const [whatsappPending, setWhatsappPending] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);

  // Get unique coaches for filter dropdown
  const uniqueCoaches = useMemo(() => {
    const coaches = clients
      .map(c => c.assigned_coach)
      .filter(coach => coach && coach !== 'Unassigned');
    return [...new Set(coaches)];
  }, [clients]);

  // Filtered clients based on search and filters
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Search filter (name, email, client_id)
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        client.full_name?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower) ||
        client.client_id?.toLowerCase().includes(searchLower) ||
        client.phone?.toLowerCase().includes(searchLower);
      
      // Program filter
      const matchesProgram = programFilter === 'all' || client.program_type === programFilter;
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
      
      // Coach filter
      const matchesCoach = coachFilter === 'all' || 
        (coachFilter === 'unassigned' && !client.assigned_coach) ||
        client.assigned_coach === coachFilter;
      
      return matchesSearch && matchesProgram && matchesStatus && matchesCoach;
    });
  }, [clients, searchQuery, programFilter, statusFilter, coachFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setProgramFilter('all');
    setStatusFilter('all');
    setCoachFilter('all');
  };

  const hasActiveFilters = searchQuery || programFilter !== 'all' || statusFilter !== 'all' || coachFilter !== 'all';

  useEffect(() => {
    fetchData();
    fetchRemindersData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, reportsRes, clientsRes, alertsRes, lowSessionsRes, coachesRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/reports/dashboard'),
        api.get('/clients'),
        api.get('/alerts/attendance'),
        api.get('/alerts/sessions-low'),
        api.get('/coaches')
      ]);
      
      setStats(statsRes.data);
      setReports(reportsRes.data);
      setClients(clientsRes.data.clients);
      setAlerts(alertsRes.data.alerts);
      setLowSessionsClients(lowSessionsRes.data.clients);
      setCoaches(coachesRes.data.coaches || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const fetchRemindersData = async () => {
    try {
      const [pendingRes, historyRes, whatsappRes] = await Promise.all([
        remindersAPI.getPending(),
        remindersAPI.getHistory(20),
        whatsappAPI.getPending()
      ]);
      
      setPendingReminders(pendingRes.data);
      setReminderHistory(historyRes.data.reminders || []);
      setWhatsappPending(whatsappRes.data.pending || []);
    } catch (error) {
      console.error('Error fetching reminders data:', error);
    }
  };

  const sendEmailReminder = async (clientId, reminderType) => {
    setSendingReminder(clientId);
    try {
      await remindersAPI.sendReminder({ client_id: clientId, reminder_type: reminderType });
      toast.success('Reminder email sent successfully!');
      fetchRemindersData();
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const sendBulkReminders = async (reminderType) => {
    setLoading(true);
    try {
      const result = await remindersAPI.sendBulkReminders({ reminder_type: reminderType, send_to_all: true });
      toast.success(`Sent ${result.data.sent} reminders successfully!`);
      fetchRemindersData();
    } catch (error) {
      console.error('Error sending bulk reminders:', error);
      toast.error('Failed to send bulk reminders');
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = async (clientId, template) => {
    try {
      const result = await whatsappAPI.generateLink({ client_id: clientId, message_template: template });
      window.open(result.data.whatsapp_link, '_blank');
      toast.success(`WhatsApp opened for ${result.data.client_name}`);
      fetchRemindersData();
    } catch (error) {
      console.error('Error generating WhatsApp link:', error);
      toast.error(error.response?.data?.detail || 'Failed to open WhatsApp');
    }
  };

  const createCoach = async () => {
    if (!newCoach.full_name || !newCoach.email || !newCoach.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/coaches', newCoach);
      toast.success(`Coach ${newCoach.full_name} created successfully!`);
      setNewCoach({
        full_name: '',
        email: '',
        phone: '',
        specialization: 'all',
        bio: '',
        certifications: '',
        create_login: true,
        password: 'coach123'
      });
      fetchData();
    } catch (error) {
      console.error('Error creating coach:', error);
      toast.error(error.response?.data?.detail || 'Failed to create coach');
    } finally {
      setLoading(false);
    }
  };

  // Deactivate Coach (soft delete)
  const deactivateCoach = async (coachId) => {
    if (!window.confirm('Are you sure you want to deactivate this coach?')) return;

    setLoading(true);
    try {
      await api.delete(`/coaches/${coachId}`);
      toast.success('Coach deactivated successfully');
      fetchData();
    } catch (error) {
      console.error('Error deactivating coach:', error);
      toast.error(error.response?.data?.detail || 'Failed to deactivate coach');
    } finally {
      setLoading(false);
    }
  };

  // Permanently Delete Coach
  const deleteCoachPermanently = async (coachId, coachName) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${coachName}? This will remove their profile, login account, and unassign all their clients. This action cannot be undone.`)) return;

    setLoading(true);
    try {
      await api.delete(`/coaches/${coachId}/permanent`);
      toast.success(`Coach ${coachName} permanently deleted`);
      fetchData();
    } catch (error) {
      console.error('Error deleting coach:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete coach');
    } finally {
      setLoading(false);
    }
  };

  // Reactivate Coach
  const reactivateCoach = async (coachId) => {
    setLoading(true);
    try {
      await api.patch(`/coaches/${coachId}/reactivate`);
      toast.success('Coach reactivated successfully');
      fetchData();
    } catch (error) {
      console.error('Error reactivating coach:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate coach');
    } finally {
      setLoading(false);
    }
  };

  // Register New Client (Manual)
  const registerClient = async () => {
    if (!newClient.full_name || !newClient.email || !newClient.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      // Convert 'none' to empty string for assigned_coach
      const clientData = {
        ...newClient,
        assigned_coach: newClient.assigned_coach === 'none' ? '' : newClient.assigned_coach
      };
      await api.post('/clients/register', clientData);
      toast.success(`Client ${newClient.full_name} registered successfully!`);
      setNewClient({
        full_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        parent_name: '',
        parent_email: '',
        parent_phone: '',
        program_type: 'children',
        package_type: 'package',
        sessions_purchased: 12,
        amount_expected: 150000,
        amount_paid: 0,
        discount_applied: 0,
        payment_status: 'pending',
        assigned_coach: 'none',
        assigned_schedule: '',
        start_date: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error registering client:', error);
      toast.error(error.response?.data?.detail || 'Failed to register client');
    } finally {
      setLoading(false);
    }
  };

  // Parse CSV File
  const parseCSV = (text) => {
    const lines = text.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const clients = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const values = lines[i].split(',').map(v => v.trim());
      const client = {};
      headers.forEach((header, idx) => {
        client[header] = values[idx] || '';
      });
      clients.push(client);
    }
    return clients;
  };

  // Handle CSV File Upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      setImportResults(null);
    }
  };

  // Bulk Import Clients
  const bulkImportClients = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file first');
      return;
    }
    
    setLoading(true);
    try {
      const text = await csvFile.text();
      const clients = parseCSV(text);
      
      if (clients.length === 0) {
        toast.error('No valid clients found in CSV');
        setLoading(false);
        return;
      }
      
      const response = await api.post('/clients/bulk-import', { clients });
      setImportResults(response.data);
      toast.success(`Imported ${response.data.imported} clients successfully!`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} clients failed to import`);
      }
      fetchData();
      setCsvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Error importing clients:', error);
      toast.error(error.response?.data?.detail || 'Failed to import clients');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Revenue Report
  const fetchRevenueReport = async () => {
    try {
      const response = await api.get('/reports/revenue');
      setRevenueReport(response.data);
    } catch (error) {
      console.error('Error fetching revenue report:', error);
    }
  };

  // Issue Certificate
  const issueCertificate = async (clientId, clientName) => {
    if (!window.confirm(`Issue completion certificate to ${clientName}?`)) return;
    
    setLoading(true);
    try {
      await api.post(`/clients/${clientId}/issue-certificate`);
      toast.success(`Certificate issued to ${clientName}!`);
      fetchData();
    } catch (error) {
      console.error('Error issuing certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to issue certificate');
    } finally {
      setLoading(false);
    }
  };

  // Download Certificate PDF
  const downloadCertificate = async (clientId, clientName) => {
    try {
      const response = await api.get(`/clients/${clientId}/certificate-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${clientId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Certificate downloaded for ${clientName}`);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to download certificate. Make sure certificate has been issued first.');
    }
  };

  // Send Automated Birthday Emails
  const sendBirthdayEmails = async () => {
    setLoading(true);
    try {
      const response = await api.post('/birthdays/send-automated');
      if (response.data.birthday_count > 0) {
        toast.success(`Sent birthday emails to ${response.data.birthday_count} client(s)!`);
      } else {
        toast.info('No birthdays today or all emails already sent');
      }
    } catch (error) {
      console.error('Error sending birthday emails:', error);
      toast.error('Failed to send birthday emails');
    } finally {
      setLoading(false);
    }
  };

  // Email Templates Functions
  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/email-templates');
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const previewTemplate = async (templateName) => {
    try {
      const response = await api.post(`/email-templates/${templateName}/preview`);
      setTemplatePreview(response.data);
    } catch (error) {
      console.error('Error previewing template:', error);
      toast.error('Failed to preview template');
    }
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    
    setLoading(true);
    try {
      await api.patch(`/email-templates/${editingTemplate.name}`, {
        subject: editingTemplate.subject,
        body: editingTemplate.body,
        is_active: editingTemplate.is_active
      });
      toast.success('Template saved successfully!');
      setEditingTemplate(null);
      fetchEmailTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const resetDefaultTemplates = async () => {
    if (!window.confirm('This will reset all templates to their default values. Continue?')) return;
    
    setLoading(true);
    try {
      await api.post('/email-templates/reset-defaults');
      toast.success('Templates reset to defaults!');
      fetchEmailTemplates();
    } catch (error) {
      console.error('Error resetting templates:', error);
      toast.error('Failed to reset templates');
    } finally {
      setLoading(false);
    }
  };

  const assignClient = async () => {
    if (!selectedClient) return;
    
    setLoading(true);
    try {
      await api.post(`/clients/${selectedClient.client_id}/assign`, assignment);
      toast.success('Client assigned successfully!');
      setSelectedClient(null);
      setAssignment({
        coach_name: '',
        coach_email: '',
        schedule_days: '',
        schedule_times: '',
        start_date: '',
        program_details: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error assigning client:', error);
      toast.error('Failed to assign client');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    setLoading(true);
    try {
      await api.post('/messages/send', message);
      toast.success('Message sent successfully!');
      setMessage({
        recipient_phone: '',
        recipient_email: '',
        message: '',
        send_via: 'email'
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  if (!reports) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-xl text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} onLogout={onLogout} />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Control Center</h1>
            <p className="text-lg text-slate-600">Comprehensive client management & operations dashboard</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card data-testid="stat-clients">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Total Clients</CardTitle>
                <Users className="w-5 h-5 text-cyan-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">{reports.total_clients}</div>
                <p className="text-xs text-slate-500 mt-1">
                  {reports.active_clients} active, {reports.inactive_clients} inactive
                </p>
              </CardContent>
            </Card>

            <Card data-testid="stat-attendance">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Today&apos;s Attendance</CardTitle>
                <Calendar className="w-5 h-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">{reports.today_attendance.attended}/{reports.today_attendance.total}</div>
                <p className="text-xs text-slate-500 mt-1">
                  No-show: {reports.today_attendance.no_show_rate}%
                </p>
              </CardContent>
            </Card>

            <Card data-testid="stat-alerts">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Needs Follow-up</CardTitle>
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">{reports.needs_follow_up}</div>
                <p className="text-xs text-slate-500 mt-1">Clients requiring attention</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-programs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">By Program</CardTitle>
                <Trophy className="w-5 h-5 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-sm text-slate-700">
                  <div>Children: {reports.by_program.children}</div>
                  <div>Adults: {reports.by_program.adults}</div>
                  <div>Marlins: {reports.by_program.marlins}</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="clients" className="space-y-6">
            <TabsList className="grid w-full grid-cols-10">
              <TabsTrigger value="clients">Clients</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
              <TabsTrigger value="import">Import</TabsTrigger>
              <TabsTrigger value="coaches">Coaches</TabsTrigger>
              <TabsTrigger value="birthdays">Birthdays</TabsTrigger>
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
              <TabsTrigger value="templates" onClick={fetchEmailTemplates}>Templates</TabsTrigger>
              <TabsTrigger value="reminders">Reminders</TabsTrigger>
              <TabsTrigger value="assign">Assign</TabsTrigger>
              <TabsTrigger value="messaging">Messages</TabsTrigger>
            </TabsList>

            {/* All Clients Tab */}
            <TabsContent value="clients">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Client Management</span>
                    <span className="text-sm font-normal text-slate-500">
                      {filteredClients.length} of {clients.length} clients
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Search and Filter Section */}
                  <div className="mb-6 space-y-4">
                    {/* Search Bar */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="Search by name, email, client ID, or phone..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10"
                        data-testid="client-search-input"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Filter Row */}
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium text-slate-600">Filters:</span>
                      </div>
                      
                      {/* Program Filter */}
                      <Select value={programFilter} onValueChange={setProgramFilter}>
                        <SelectTrigger className="w-[150px]" data-testid="program-filter">
                          <SelectValue placeholder="Program" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-white">
                          <SelectItem value="all">All Programs</SelectItem>
                          <SelectItem value="children">Children</SelectItem>
                          <SelectItem value="adults">Adults</SelectItem>
                          <SelectItem value="marlins">Marlins</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Status Filter */}
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[140px]" data-testid="status-filter">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-white">
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="dormant">Dormant</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Coach Filter */}
                      <Select value={coachFilter} onValueChange={setCoachFilter}>
                        <SelectTrigger className="w-[160px]" data-testid="coach-filter">
                          <SelectValue placeholder="Coach" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-white">
                          <SelectItem value="all">All Coaches</SelectItem>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {uniqueCoaches.map(coach => (
                            <SelectItem key={coach} value={coach}>{coach}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Clear Filters Button */}
                      {hasActiveFilters && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearFilters}
                          className="text-slate-600"
                          data-testid="clear-filters-btn"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear Filters
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="clients-table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 text-slate-600 font-semibold">Client ID</th>
                          <th className="text-left p-3 text-slate-600 font-semibold">Name</th>
                          <th className="text-left p-3 text-slate-600 font-semibold">Program</th>
                          <th className="text-left p-3 text-slate-600 font-semibold">Coach</th>
                          <th className="text-left p-3 text-slate-600 font-semibold">Sessions</th>
                          <th className="text-left p-3 text-slate-600 font-semibold">Status</th>
                          <th className="text-left p-3 text-slate-600 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredClients.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="p-8 text-center text-slate-500">
                              {hasActiveFilters ? (
                                <div>
                                  <p>No clients match your filters</p>
                                  <Button 
                                    variant="link" 
                                    onClick={clearFilters}
                                    className="mt-2"
                                  >
                                    Clear filters
                                  </Button>
                                </div>
                              ) : (
                                <p>No clients found</p>
                              )}
                            </td>
                          </tr>
                        ) : (
                          filteredClients.map((client) => (
                            <tr key={client.client_id} className="border-b hover:bg-slate-50">
                              <td className="p-3 font-mono text-sm">{client.client_id}</td>
                              <td className="p-3">
                                <div className="font-medium text-slate-900">{client.full_name}</div>
                                <div className="text-sm text-slate-500">{client.email}</div>
                                {client.phone && <div className="text-xs text-slate-400">{client.phone}</div>}
                              </td>
                              <td className="p-3">
                                <Badge variant="outline" className={
                                  client.program_type === 'marlins' ? 'border-orange-300 text-orange-700' :
                                  client.program_type === 'adults' ? 'border-blue-300 text-blue-700' :
                                  'border-cyan-300 text-cyan-700'
                                }>
                                  {client.program_type}
                                </Badge>
                              </td>
                              <td className="p-3">
                                {client.assigned_coach ? (
                                  <span className="text-slate-700">{client.assigned_coach}</span>
                                ) : (
                                  <span className="text-slate-400 italic">Unassigned</span>
                                )}
                              </td>
                              <td className="p-3">
                                <span className={client.sessions_remaining <= 3 ? 'text-red-600 font-semibold' : ''}>
                                  {client.sessions_remaining}/{client.sessions_purchased}
                                </span>
                              </td>
                              <td className="p-3">
                                <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
                                  {client.status}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center space-x-1">
                                  {client.certificate_issued ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-green-600 border-green-200 hover:bg-green-50"
                                      onClick={() => downloadCertificate(client.client_id, client.full_name)}
                                      title="Download Certificate"
                                      data-testid={`download-cert-${client.client_id}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                      onClick={() => issueCertificate(client.client_id, client.full_name)}
                                      title="Issue Certificate"
                                      data-testid={`issue-cert-${client.client_id}`}
                                    >
                                      <Award className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedClient(client)}
                                    title="Assign Coach"
                                    data-testid={`assign-${client.client_id}`}
                                  >
                                    <UserPlus className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Register New Client Tab */}
            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <UserPlus className="w-5 h-5 mr-2 text-cyan-600" />
                    Register New Client (Manual Entry)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="client_name">Full Name *</Label>
                      <Input
                        id="client_name"
                        value={newClient.full_name}
                        onChange={(e) => setNewClient({...newClient, full_name: e.target.value})}
                        placeholder="Client's full name"
                        data-testid="new-client-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_email">Email *</Label>
                      <Input
                        id="client_email"
                        type="email"
                        value={newClient.email}
                        onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                        placeholder="client@example.com"
                        data-testid="new-client-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_phone">Phone *</Label>
                      <Input
                        id="client_phone"
                        value={newClient.phone}
                        onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                        placeholder="+233..."
                        data-testid="new-client-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_dob">Date of Birth</Label>
                      <Input
                        id="client_dob"
                        type="date"
                        value={newClient.date_of_birth}
                        onChange={(e) => setNewClient({...newClient, date_of_birth: e.target.value})}
                        data-testid="new-client-dob"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_program">Program Type *</Label>
                      <Select 
                        value={newClient.program_type} 
                        onValueChange={(val) => setNewClient({...newClient, program_type: val})}
                      >
                        <SelectTrigger data-testid="new-client-program">
                          <SelectValue placeholder="Select program" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-white">
                          <SelectItem value="children">Children</SelectItem>
                          <SelectItem value="adults">Adults</SelectItem>
                          <SelectItem value="marlins">Marlins Team</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="client_package">Package Type</Label>
                      <Select 
                        value={newClient.package_type} 
                        onValueChange={(val) => setNewClient({...newClient, package_type: val})}
                      >
                        <SelectTrigger data-testid="new-client-package">
                          <SelectValue placeholder="Select package" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-white">
                          <SelectItem value="per_session">Per Session</SelectItem>
                          <SelectItem value="package">12 Sessions Package</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="client_sessions">Sessions Purchased</Label>
                      <Input
                        id="client_sessions"
                        type="number"
                        value={newClient.sessions_purchased}
                        onChange={(e) => setNewClient({...newClient, sessions_purchased: parseInt(e.target.value) || 0})}
                        data-testid="new-client-sessions"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_amount">Amount Expected (GHS)</Label>
                      <Input
                        id="client_amount"
                        type="number"
                        value={newClient.amount_expected / 100}
                        onChange={(e) => setNewClient({...newClient, amount_expected: (parseFloat(e.target.value) || 0) * 100})}
                        data-testid="new-client-amount"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_paid">Amount Paid (GHS)</Label>
                      <Input
                        id="client_paid"
                        type="number"
                        value={newClient.amount_paid / 100}
                        onChange={(e) => setNewClient({...newClient, amount_paid: (parseFloat(e.target.value) || 0) * 100})}
                        data-testid="new-client-paid"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_payment_status">Payment Status</Label>
                      <Select 
                        value={newClient.payment_status} 
                        onValueChange={(val) => setNewClient({...newClient, payment_status: val})}
                      >
                        <SelectTrigger data-testid="new-client-payment-status">
                          <SelectValue placeholder="Payment status" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-white">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="partial">Partial</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="client_coach">Assign Coach</Label>
                      <Select 
                        value={newClient.assigned_coach} 
                        onValueChange={(val) => setNewClient({...newClient, assigned_coach: val})}
                      >
                        <SelectTrigger data-testid="new-client-coach">
                          <SelectValue placeholder="Select coach" />
                        </SelectTrigger>
                        <SelectContent className="z-50 bg-white">
                          <SelectItem value="none">Unassigned</SelectItem>
                          {coaches.filter(c => c.status === 'active').map((coach) => (
                            <SelectItem key={coach.coach_id} value={coach.full_name}>{coach.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="client_start">Start Date</Label>
                      <Input
                        id="client_start"
                        type="date"
                        value={newClient.start_date}
                        onChange={(e) => setNewClient({...newClient, start_date: e.target.value})}
                        data-testid="new-client-start"
                      />
                    </div>
                  </div>
                  
                  {/* Parent Info Section */}
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="font-semibold text-slate-700 mb-4">Parent/Guardian Information (for children)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="parent_name">Parent Name</Label>
                        <Input
                          id="parent_name"
                          value={newClient.parent_name}
                          onChange={(e) => setNewClient({...newClient, parent_name: e.target.value})}
                          placeholder="Parent's full name"
                          data-testid="new-client-parent-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="parent_email">Parent Email</Label>
                        <Input
                          id="parent_email"
                          type="email"
                          value={newClient.parent_email}
                          onChange={(e) => setNewClient({...newClient, parent_email: e.target.value})}
                          placeholder="parent@example.com"
                          data-testid="new-client-parent-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="parent_phone">Parent Phone</Label>
                        <Input
                          id="parent_phone"
                          value={newClient.parent_phone}
                          onChange={(e) => setNewClient({...newClient, parent_phone: e.target.value})}
                          placeholder="+233..."
                          data-testid="new-client-parent-phone"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={registerClient}
                    disabled={loading}
                    className="w-full mt-6 bg-cyan-600 hover:bg-cyan-700"
                    data-testid="register-client-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {loading ? 'Registering...' : 'Register Client'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Bulk Import Tab */}
            <TabsContent value="import">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Upload className="w-5 h-5 mr-2 text-cyan-600" />
                      Bulk Import Clients (CSV)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* CSV Format Info */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">CSV Format Requirements</h4>
                        <p className="text-sm text-blue-700 mb-2">
                          Your CSV file should have the following columns (header row required):
                        </p>
                        <code className="text-xs bg-blue-100 p-2 rounded block overflow-x-auto">
                          full_name, email, phone, date_of_birth, parent_name, parent_email, parent_phone, program_type, package_type, sessions_purchased, sessions_remaining, amount_expected, amount_paid, payment_status, assigned_coach, start_date, status
                        </code>
                        <p className="text-xs text-blue-600 mt-2">
                          * Required columns: full_name, email, phone. Other columns are optional.
                        </p>
                      </div>
                      
                      {/* File Upload */}
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileUpload}
                          ref={fileInputRef}
                          className="hidden"
                          id="csv-upload"
                          data-testid="csv-file-input"
                        />
                        <label htmlFor="csv-upload" className="cursor-pointer">
                          <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                          <p className="text-lg font-medium text-slate-700">
                            {csvFile ? csvFile.name : 'Click to upload CSV file'}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            or drag and drop
                          </p>
                        </label>
                      </div>
                      
                      {csvFile && (
                        <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-6 h-6 text-cyan-600" />
                            <div>
                              <p className="font-medium text-slate-700">{csvFile.name}</p>
                              <p className="text-sm text-slate-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCsvFile(null);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      <Button
                        onClick={bulkImportClients}
                        disabled={loading || !csvFile}
                        className="w-full bg-cyan-600 hover:bg-cyan-700"
                        data-testid="import-clients-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {loading ? 'Importing...' : 'Import Clients'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Import Results */}
                {importResults && (
                  <Card className={importResults.failed > 0 ? 'border-amber-300' : 'border-green-300'}>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        {importResults.failed === 0 ? (
                          <Badge className="bg-green-100 text-green-700">Success</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Partial Success</Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <p className="text-3xl font-bold text-green-600">{importResults.imported}</p>
                          <p className="text-sm text-slate-600">Imported Successfully</p>
                        </div>
                        <div className="text-center p-4 bg-red-50 rounded-lg">
                          <p className="text-3xl font-bold text-red-600">{importResults.failed}</p>
                          <p className="text-sm text-slate-600">Failed</p>
                        </div>
                      </div>
                      {importResults.errors && importResults.errors.length > 0 && (
                        <div className="bg-red-50 p-4 rounded-lg">
                          <h4 className="font-semibold text-red-700 mb-2">Errors:</h4>
                          <ul className="text-sm text-red-600 space-y-1">
                            {importResults.errors.map((error, idx) => (
                              <li key={idx}>• {error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Coaches Management Tab */}
            <TabsContent value="coaches">
              <div className="space-y-6">
                {/* Add New Coach Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <UserPlus className="w-5 h-5 mr-2 text-cyan-600" />
                      Register New Coach
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="coach_name">Full Name *</Label>
                        <Input
                          id="coach_name"
                          value={newCoach.full_name}
                          onChange={(e) => setNewCoach({...newCoach, full_name: e.target.value})}
                          placeholder="Coach full name"
                          data-testid="coach-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="coach_email">Email *</Label>
                        <Input
                          id="coach_email"
                          type="email"
                          value={newCoach.email}
                          onChange={(e) => setNewCoach({...newCoach, email: e.target.value})}
                          placeholder="coach@example.com"
                          data-testid="coach-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="coach_phone">Phone *</Label>
                        <Input
                          id="coach_phone"
                          value={newCoach.phone}
                          onChange={(e) => setNewCoach({...newCoach, phone: e.target.value})}
                          placeholder="+233..."
                          data-testid="coach-phone-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="coach_specialization">Specialization</Label>
                        <Select 
                          value={newCoach.specialization} 
                          onValueChange={(val) => setNewCoach({...newCoach, specialization: val})}
                        >
                          <SelectTrigger data-testid="coach-specialization-select">
                            <SelectValue placeholder="Select specialization" />
                          </SelectTrigger>
                          <SelectContent className="z-50 bg-white">
                            <SelectItem value="all">All Programs</SelectItem>
                            <SelectItem value="children">Children Only</SelectItem>
                            <SelectItem value="adults">Adults Only</SelectItem>
                            <SelectItem value="competitive">Competitive/Marlins</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="coach_certifications">Certifications</Label>
                        <Input
                          id="coach_certifications"
                          value={newCoach.certifications}
                          onChange={(e) => setNewCoach({...newCoach, certifications: e.target.value})}
                          placeholder="e.g., Lifeguard, Swimming Instructor"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          onClick={createCoach}
                          disabled={loading}
                          className="w-full bg-cyan-600 hover:bg-cyan-700"
                          data-testid="create-coach-btn"
                        >
                          {loading ? 'Creating...' : 'Add Coach'}
                        </Button>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label htmlFor="coach_bio">Bio (Optional)</Label>
                      <Textarea
                        id="coach_bio"
                        value={newCoach.bio}
                        onChange={(e) => setNewCoach({...newCoach, bio: e.target.value})}
                        placeholder="Brief bio about the coach..."
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Coaches List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Registered Coaches</span>
                      <span className="text-sm font-normal text-slate-500">{coaches.length} coaches</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {coaches.length === 0 ? (
                      <p className="text-center py-8 text-slate-500">No coaches registered yet. Add your first coach above.</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {coaches.map((coach) => (
                          <Card key={coach.coach_id} className={`border-2 ${coach.status === 'active' ? 'border-green-200' : 'border-slate-200'}`}>
                            <CardContent className="pt-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold text-slate-900">{coach.full_name}</h4>
                                  <p className="text-sm text-slate-500">{coach.email}</p>
                                  <p className="text-xs text-slate-400">{coach.phone}</p>
                                </div>
                                <Badge variant={coach.status === 'active' ? 'default' : 'secondary'}>
                                  {coach.status}
                                </Badge>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-600">Specialization:</span>
                                  <Badge variant="outline" className="capitalize">{coach.specialization}</Badge>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-600">Assigned Clients:</span>
                                  <span className="font-semibold text-cyan-600">{coach.assigned_clients_count || 0}</span>
                                </div>
                                {coach.certifications && (
                                  <p className="text-xs text-slate-500 pt-2 border-t">
                                    Certifications: {coach.certifications}
                                  </p>
                                )}
                              </div>
                              {coach.assigned_clients && coach.assigned_clients.length > 0 && (
                                <div className="mt-3 pt-3 border-t">
                                  <p className="text-xs font-semibold text-slate-600 mb-1">Assigned Clients:</p>
                                  <div className="max-h-24 overflow-y-auto">
                                    {coach.assigned_clients.map((c) => (
                                      <div key={c.client_id} className="text-xs text-slate-500 py-0.5">
                                        {c.full_name} ({c.program_type})
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Coach Actions */}
                              <div className="mt-4 pt-3 border-t flex justify-end gap-2">
                                {coach.status === 'active' ? (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                      onClick={() => deactivateCoach(coach.coach_id)}
                                      disabled={loading}
                                    >
                                      Deactivate
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-red-600 border-red-200 hover:bg-red-50"
                                      onClick={() => deleteCoachPermanently(coach.coach_id, coach.full_name)}
                                      disabled={loading}
                                      data-testid={`delete-coach-${coach.coach_id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      Delete
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-green-600 border-green-200 hover:bg-green-50"
                                      onClick={() => reactivateCoach(coach.coach_id)}
                                      disabled={loading}
                                    >
                                      Reactivate
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-red-600 border-red-200 hover:bg-red-50"
                                      onClick={() => deleteCoachPermanently(coach.coach_id, coach.full_name)}
                                      disabled={loading}
                                      data-testid={`delete-coach-${coach.coach_id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Birthdays Tab */}
            <TabsContent value="birthdays">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center">
                        <Gift className="w-5 h-5 mr-2 text-amber-600" />
                        Birthday Management
                      </span>
                      <Button 
                        onClick={sendBirthdayEmails}
                        disabled={loading}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="send-birthday-emails-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {loading ? 'Sending...' : 'Send Birthday Emails Today'}
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-amber-800 mb-2">🎂 Automated Birthday Emails</h4>
                      <p className="text-sm text-amber-700">
                        Click the button above to send birthday emails to all clients with birthdays today.
                        The system will check for clients whose birthdays match today's date and send them
                        a personalized birthday message.
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-700">Clients with Birthdays (from client list)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {clients.filter(c => c.date_of_birth).map((client) => {
                          const dob = new Date(client.date_of_birth);
                          const today = new Date();
                          const isToday = dob.getMonth() === today.getMonth() && dob.getDate() === today.getDate();
                          const age = today.getFullYear() - dob.getFullYear();
                          
                          return (
                            <div 
                              key={client.client_id}
                              className={`p-4 rounded-lg border-2 ${isToday ? 'border-amber-400 bg-amber-50' : 'border-slate-100 bg-white'}`}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <h5 className="font-semibold text-slate-900">{client.full_name}</h5>
                                  <p className="text-sm text-slate-600">
                                    {dob.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                                  </p>
                                  <p className="text-xs text-slate-400">Age: {age} years</p>
                                </div>
                                {isToday && (
                                  <Badge className="bg-amber-500 text-white">
                                    🎂 Today!
                                  </Badge>
                                )}
                              </div>
                              {client.certificate_issued && (
                                <Badge variant="outline" className="mt-2 text-green-600 border-green-300">
                                  <Award className="w-3 h-3 mr-1" />
                                  Certified
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                        {clients.filter(c => c.date_of_birth).length === 0 && (
                          <div className="col-span-3 text-center py-8 text-slate-500">
                            <Cake className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                            <p>No clients with birthday information on file</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Revenue Report Tab */}
            <TabsContent value="revenue">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center">
                        <BarChart3 className="w-5 h-5 mr-2 text-green-600" />
                        Revenue Report
                      </span>
                      <Button variant="outline" size="sm" onClick={fetchRevenueReport}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!revenueReport ? (
                      <div className="text-center py-12">
                        <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                        <p className="text-slate-600 mb-4">Click refresh to load revenue report</p>
                        <Button onClick={fetchRevenueReport}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Load Report
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Totals Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Card className="bg-linear-to-br from-green-50 to-white border-green-200">
                            <CardContent className="pt-6">
                              <p className="text-sm text-slate-600">Total Expected</p>
                              <p className="text-3xl font-bold text-green-700">
                                GHS {(revenueReport.totals?.total_expected / 100 || 0).toLocaleString()}
                              </p>
                            </CardContent>
                          </Card>
                          <Card className="bg-linear-to-br from-cyan-50 to-white border-cyan-200">
                            <CardContent className="pt-6">
                              <p className="text-sm text-slate-600">Total Received</p>
                              <p className="text-3xl font-bold text-cyan-700">
                                GHS {(revenueReport.totals?.total_paid / 100 || 0).toLocaleString()}
                              </p>
                            </CardContent>
                          </Card>
                          <Card className="bg-linear-to-br from-amber-50 to-white border-amber-200">
                            <CardContent className="pt-6">
                              <p className="text-sm text-slate-600">Outstanding</p>
                              <p className="text-3xl font-bold text-amber-700">
                                GHS {(revenueReport.totals?.total_outstanding / 100 || 0).toLocaleString()}
                              </p>
                            </CardContent>
                          </Card>
                        </div>
                        
                        {/* Revenue by Program */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Revenue by Program</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              {Object.entries(revenueReport.by_program || {}).map(([program, data]) => (
                                <div key={program} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                  <div className="flex items-center space-x-4">
                                    <Badge variant="outline" className="capitalize">{program}</Badge>
                                    <span className="text-sm text-slate-600">{data.count} clients</span>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold text-slate-900">
                                      GHS {(data.paid / 100).toLocaleString()} / {(data.expected / 100).toLocaleString()}
                                    </p>
                                    <div className="w-32 h-2 bg-slate-200 rounded-full mt-1">
                                      <div 
                                        className="h-full bg-cyan-500 rounded-full"
                                        style={{ width: `${data.expected > 0 ? (data.paid / data.expected * 100) : 0}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                        
                        {/* Outstanding Payments */}
                        {revenueReport.by_status?.pending?.length > 0 && (
                          <Card className="border-amber-200">
                            <CardHeader>
                              <CardTitle className="text-lg text-amber-700">Pending Payments</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead>
                                    <tr className="border-b">
                                      <th className="text-left p-2 text-slate-600">Client</th>
                                      <th className="text-left p-2 text-slate-600">Program</th>
                                      <th className="text-right p-2 text-slate-600">Expected</th>
                                      <th className="text-right p-2 text-slate-600">Paid</th>
                                      <th className="text-right p-2 text-slate-600">Outstanding</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {revenueReport.by_status.pending.slice(0, 10).map((client) => (
                                      <tr key={client.client_id} className="border-b hover:bg-slate-50">
                                        <td className="p-2">
                                          <p className="font-medium">{client.full_name}</p>
                                          <p className="text-xs text-slate-500">{client.client_id}</p>
                                        </td>
                                        <td className="p-2">
                                          <Badge variant="outline" className="capitalize">{client.program_type}</Badge>
                                        </td>
                                        <td className="p-2 text-right">GHS {(client.amount_expected / 100).toFixed(2)}</td>
                                        <td className="p-2 text-right">GHS {(client.amount_paid / 100).toFixed(2)}</td>
                                        <td className="p-2 text-right font-semibold text-amber-600">
                                          GHS {(client.outstanding / 100).toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {revenueReport.by_status.pending.length > 10 && (
                                  <p className="text-center text-sm text-slate-500 mt-4">
                                    Showing 10 of {revenueReport.by_status.pending.length} pending payments
                                  </p>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Email Templates Tab */}
            <TabsContent value="templates">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center">
                        <Mail className="w-5 h-5 mr-2 text-cyan-600" />
                        Email Templates
                      </span>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={fetchEmailTemplates}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh
                        </Button>
                        <Button variant="outline" size="sm" onClick={resetDefaultTemplates}>
                          Reset Defaults
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-blue-800 mb-2">📧 Template Customization</h4>
                      <p className="text-sm text-blue-700">
                        Customize the email templates sent to clients. Use placeholders like <code className="bg-blue-100 px-1 rounded">{"{{client_name}}"}</code>, 
                        <code className="bg-blue-100 px-1 rounded">{"{{parent_name}}"}</code>, <code className="bg-blue-100 px-1 rounded">{"{{sessions_remaining}}"}</code> etc.
                      </p>
                    </div>
                    
                    {emailTemplates.length === 0 ? (
                      <div className="text-center py-12">
                        <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                        <p className="text-slate-600 mb-4">Click Refresh to load email templates</p>
                        <Button onClick={fetchEmailTemplates}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Load Templates
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {emailTemplates.map((template) => (
                          <Card key={template.template_id} className={`border-2 ${template.is_active ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-lg flex items-center justify-between">
                                <span className="capitalize">{template.name.replace(/_/g, ' ')}</span>
                                <Badge variant={template.is_active ? 'default' : 'secondary'}>
                                  {template.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="text-sm text-slate-600 mb-3 truncate" title={template.subject}>
                                <strong>Subject:</strong> {template.subject}
                              </p>
                              <div className="flex space-x-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    setEditingTemplate({...template});
                                    setTemplatePreview(null);
                                  }}
                                  data-testid={`edit-template-${template.name}`}
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => previewTemplate(template.name)}
                                  data-testid={`preview-template-${template.name}`}
                                >
                                  Preview
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Template Preview Modal */}
                {templatePreview && (
                  <Card className="border-2 border-cyan-300">
                    <CardHeader className="bg-cyan-50">
                      <CardTitle className="flex items-center justify-between">
                        <span>Preview: {templatePreview.template_name.replace(/_/g, ' ')}</span>
                        <Button variant="outline" size="sm" onClick={() => setTemplatePreview(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="mb-4">
                        <Label className="text-slate-600">Subject:</Label>
                        <p className="font-semibold text-slate-900">{templatePreview.subject}</p>
                      </div>
                      <div>
                        <Label className="text-slate-600">Email Body:</Label>
                        <div 
                          className="mt-2 border rounded-lg p-4 bg-white"
                          dangerouslySetInnerHTML={{ __html: templatePreview.body }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Template Edit Modal */}
                {editingTemplate && (
                  <Card className="border-2 border-amber-300">
                    <CardHeader className="bg-amber-50">
                      <CardTitle className="flex items-center justify-between">
                        <span>Edit: {editingTemplate.name.replace(/_/g, ' ')}</span>
                        <Button variant="outline" size="sm" onClick={() => setEditingTemplate(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-4">
                      <div>
                        <Label htmlFor="edit-subject">Subject</Label>
                        <Input
                          id="edit-subject"
                          value={editingTemplate.subject}
                          onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                          className="bg-white"
                          data-testid="template-subject-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-body">Email Body (HTML)</Label>
                        <Textarea
                          id="edit-body"
                          value={editingTemplate.body}
                          onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})}
                          rows={12}
                          className="bg-white font-mono text-sm"
                          data-testid="template-body-input"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="edit-active"
                          checked={editingTemplate.is_active}
                          onChange={(e) => setEditingTemplate({...editingTemplate, is_active: e.target.checked})}
                          className="rounded"
                        />
                        <Label htmlFor="edit-active">Template Active</Label>
                      </div>
                      <div className="flex space-x-3 pt-4">
                        <Button 
                          onClick={saveTemplate} 
                          disabled={loading}
                          className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                          data-testid="save-template-btn"
                        >
                          {loading ? 'Saving...' : 'Save Template'}
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => setEditingTemplate(null)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Reminders & WhatsApp Tab */}
            <TabsContent value="reminders">
              <div className="space-y-6">
                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-linear-to-br from-amber-50 to-white border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center text-amber-700">
                        <Clock className="w-5 h-5 mr-2" />
                        Low Sessions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-amber-600">{pendingReminders.low_sessions?.length || 0}</p>
                      <p className="text-sm text-slate-600 mt-1">Clients with ≤3 sessions</p>
                      {pendingReminders.low_sessions?.length > 0 && (
                        <Button 
                          size="sm" 
                          className="mt-3 bg-amber-600 hover:bg-amber-700"
                          onClick={() => sendBulkReminders('low_sessions')}
                          disabled={loading}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Email All
                        </Button>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-linear-to-br from-red-50 to-white border-red-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center text-red-700">
                        <AlertTriangle className="w-5 h-5 mr-2" />
                        Missed Sessions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-red-600">{pendingReminders.missed_sessions?.length || 0}</p>
                      <p className="text-sm text-slate-600 mt-1">2+ consecutive misses</p>
                      {pendingReminders.missed_sessions?.length > 0 && (
                        <Button 
                          size="sm" 
                          className="mt-3 bg-red-600 hover:bg-red-700"
                          onClick={() => sendBulkReminders('missed_session')}
                          disabled={loading}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Email All
                        </Button>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-linear-to-br from-slate-50 to-white border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center text-slate-700">
                        <Users className="w-5 h-5 mr-2" />
                        Inactive (7+ days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-slate-600">{pendingReminders.inactive_7_days?.length || 0}</p>
                      <p className="text-sm text-slate-600 mt-1">No recent attendance</p>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="mt-3"
                        onClick={fetchRemindersData}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Refresh
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Low Sessions Clients */}
                {pendingReminders.low_sessions?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-amber-700">
                        <Clock className="w-5 h-5 mr-2" />
                        Clients with Low Sessions (Need Renewal)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {pendingReminders.low_sessions.map((client) => (
                          <div key={client.client_id} className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <div>
                              <p className="font-semibold text-slate-900">{client.full_name}</p>
                              <p className="text-sm text-slate-600">{client.email}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-amber-700 border-amber-300">
                                  {client.sessions_remaining} sessions left
                                </Badge>
                                <Badge variant="outline">{client.program_type}</Badge>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendEmailReminder(client.client_id, 'low_sessions')}
                                disabled={sendingReminder === client.client_id}
                                data-testid={`email-reminder-${client.client_id}`}
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                {sendingReminder === client.client_id ? 'Sending...' : 'Email'}
                              </Button>
                              {client.phone && (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => openWhatsApp(client.client_id, 'low_sessions')}
                                  data-testid={`whatsapp-${client.client_id}`}
                                >
                                  <MessageCircle className="w-4 h-4 mr-1" />
                                  WhatsApp
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Missed Sessions Clients */}
                {pendingReminders.missed_sessions?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-red-700">
                        <AlertTriangle className="w-5 h-5 mr-2" />
                        Clients Who Missed Sessions (Follow Up Needed)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {pendingReminders.missed_sessions.map((client) => (
                          <div key={client.client_id} className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                            <div>
                              <p className="font-semibold text-slate-900">{client.full_name}</p>
                              <p className="text-sm text-slate-600">{client.email}</p>
                              <Badge variant="outline" className="mt-1 text-red-700 border-red-300">
                                {client.missed_count} sessions missed
                              </Badge>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendEmailReminder(client.client_id, 'missed_session')}
                                disabled={sendingReminder === client.client_id}
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                {sendingReminder === client.client_id ? 'Sending...' : 'Email'}
                              </Button>
                              {client.phone && (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => openWhatsApp(client.client_id, 'missed_session')}
                                >
                                  <MessageCircle className="w-4 h-4 mr-1" />
                                  WhatsApp
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* WhatsApp Pending */}
                {whatsappPending.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-green-700">
                        <MessageCircle className="w-5 h-5 mr-2" />
                        WhatsApp Quick Send
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-4">
                        Click to open WhatsApp with a pre-written message. You can edit before sending.
                      </p>
                      <div className="space-y-3">
                        {whatsappPending.slice(0, 10).map((client) => (
                          <div key={client.client_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                            <div>
                              <p className="font-medium text-slate-900">{client.full_name}</p>
                              <p className="text-sm text-slate-600">{client.phone}</p>
                              <Badge 
                                variant="outline" 
                                className={client.priority === 'high' ? 'text-red-600 border-red-300' : 'text-amber-600 border-amber-300'}
                              >
                                {client.reason === 'low_sessions' ? 'Low Sessions' : 'Missed Sessions'}
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => openWhatsApp(client.client_id, client.reason === 'low_sessions' ? 'low_sessions' : 'missed_session')}
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              Open WhatsApp
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Reminder History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Bell className="w-5 h-5 mr-2 text-cyan-600" />
                      Recent Reminder History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reminderHistory.length === 0 ? (
                      <p className="text-center py-8 text-slate-500">No reminders sent yet</p>
                    ) : (
                      <div className="space-y-2">
                        {reminderHistory.map((reminder, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900">{reminder.client_name}</p>
                              <p className="text-sm text-slate-600">{reminder.client_email}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline">
                                {reminder.reminder_type.replace('_', ' ')}
                              </Badge>
                              <p className="text-xs text-slate-500 mt-1">
                                {new Date(reminder.sent_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Empty State */}
                {pendingReminders.low_sessions?.length === 0 && 
                 pendingReminders.missed_sessions?.length === 0 && 
                 whatsappPending.length === 0 && (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Bell className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700">All Caught Up!</h3>
                      <p className="text-slate-500 mt-1">No clients need reminders right now.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                      Attendance Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {alerts.map((alert, i) => (
                        <div key={i} className="p-4 border-2 border-orange-200 rounded-xl bg-orange-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-slate-900">{alert.client_name}</h3>
                              <p className="text-sm text-slate-600 mt-1">
                                {alert.alert_type === '2_consecutive_misses' && '🔴 Missed 2 consecutive sessions'}
                                {alert.alert_type === '7_days_no_attendance' && `🔴 No attendance for ${alert.days_since} days`}
                              </p>
                              <p className="text-sm text-slate-500 mt-1">Coach: {alert.coach || 'Unassigned'}</p>
                            </div>
                            <Button size="sm" variant="outline">Contact Client</Button>
                          </div>
                        </div>
                      ))}
                      {alerts.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          No attendance alerts at this time
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                      Low Sessions Alert
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {lowSessionsClients.map((client) => (
                        <div key={client.client_id} className="p-4 border-2 border-blue-200 rounded-xl bg-blue-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-slate-900">{client.full_name}</h3>
                              <p className="text-sm text-slate-600 mt-1">
                                {client.sessions_remaining} session(s) remaining
                              </p>
                            </div>
                            <Button size="sm" variant="outline">Send Renewal</Button>
                          </div>
                        </div>
                      ))}
                      {lowSessionsClients.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          No clients with low sessions
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Assign Clients Tab */}
            <TabsContent value="assign">
              <Card>
                <CardHeader>
                  <CardTitle>Assign Client to Coach & Schedule</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Select Client</Label>
                    <select
                      className="w-full p-2 border rounded-lg"
                      value={selectedClient?.client_id || ''}
                      onChange={(e) => {
                        const client = clients.find(c => c.client_id === e.target.value);
                        setSelectedClient(client);
                      }}
                      data-testid="select-client"
                    >
                      <option value="">Choose a client...</option>
                      {clients.map((client) => (
                        <option key={client.client_id} value={client.client_id}>
                          {client.client_id} - {client.full_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedClient && (
                    <div className="space-y-4">
                      <div className="p-4 bg-cyan-50 rounded-lg">
                        <h3 className="font-semibold mb-2">{selectedClient.full_name}</h3>
                        <p className="text-sm text-slate-600">Program: {selectedClient.program_type}</p>
                        <p className="text-sm text-slate-600">Email: {selectedClient.email}</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="assign_coach">Select Coach *</Label>
                          <Select 
                            value={assignment.coach_name} 
                            onValueChange={(val) => {
                              const coach = coaches.find(c => c.full_name === val);
                              setAssignment({
                                ...assignment, 
                                coach_name: val,
                                coach_email: coach?.email || ''
                              });
                            }}
                          >
                            <SelectTrigger data-testid="assign-coach-select">
                              <SelectValue placeholder="Choose a coach..." />
                            </SelectTrigger>
                            <SelectContent className="z-50 bg-white">
                              {coaches.filter(c => c.status === 'active').map((coach) => (
                                <SelectItem key={coach.coach_id} value={coach.full_name}>
                                  {coach.full_name} ({coach.assigned_clients_count || 0} clients)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {coaches.length === 0 && (
                            <p className="text-xs text-amber-600 mt-1">
                              No coaches registered. Go to Coaches tab to add coaches first.
                            </p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="coach_email">Coach Email</Label>
                          <Input
                            id="coach_email"
                            type="email"
                            value={assignment.coach_email}
                            readOnly
                            className="bg-slate-50"
                            data-testid="coach-email-display"
                          />
                        </div>

                        <div>
                          <Label htmlFor="schedule_days">Schedule Days *</Label>
                          <Input
                            id="schedule_days"
                            value={assignment.schedule_days}
                            onChange={(e) => setAssignment({...assignment, schedule_days: e.target.value})}
                            placeholder="e.g., Monday, Wednesday, Friday"
                            data-testid="schedule-days-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="schedule_times">Schedule Times *</Label>
                          <Input
                            id="schedule_times"
                            value={assignment.schedule_times}
                            onChange={(e) => setAssignment({...assignment, schedule_times: e.target.value})}
                            placeholder="e.g., 4:00 PM - 5:00 PM"
                            data-testid="schedule-times-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="start_date">Start Date *</Label>
                          <Input
                            id="start_date"
                            type="date"
                            value={assignment.start_date}
                            onChange={(e) => setAssignment({...assignment, start_date: e.target.value})}
                            data-testid="start-date-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="program_details">Program Details</Label>
                          <Input
                            id="program_details"
                            value={assignment.program_details}
                            onChange={(e) => setAssignment({...assignment, program_details: e.target.value})}
                            placeholder="e.g., Beginner Level"
                            data-testid="program-details-input"
                          />
                        </div>
                      </div>

                      <Button
                        onClick={assignClient}
                        disabled={loading}
                        className="w-full"
                        data-testid="assign-submit-btn"
                      >
                        {loading ? 'Assigning...' : 'Assign & Notify Client'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Messaging Tab */}
            <TabsContent value="messaging">
              <Card>
                <CardHeader>
                  <CardTitle>Send Manual Message</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="recipient_email">Recipient Email</Label>
                      <Input
                        id="recipient_email"
                        type="email"
                        value={message.recipient_email}
                        onChange={(e) => setMessage({...message, recipient_email: e.target.value})}
                        data-testid="message-email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="recipient_phone">Recipient Phone</Label>
                      <Input
                        id="recipient_phone"
                        value={message.recipient_phone}
                        onChange={(e) => setMessage({...message, recipient_phone: e.target.value})}
                        placeholder="+233 XX XXX XXXX"
                        data-testid="message-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message_text">Message</Label>
                    <Textarea
                      id="message_text"
                      value={message.message}
                      onChange={(e) => setMessage({...message, message: e.target.value})}
                      rows={5}
                      placeholder="Type your message here..."
                      data-testid="message-text-input"
                    />
                  </div>

                  <div>
                    <Label>Send Via</Label>
                    <div className="flex space-x-4 mt-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="email"
                          checked={message.send_via === 'email'}
                          onChange={(e) => setMessage({...message, send_via: e.target.value})}
                          className="mr-2"
                        />
                        Email
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="whatsapp"
                          checked={message.send_via === 'whatsapp'}
                          onChange={(e) => setMessage({...message, send_via: e.target.value})}
                          className="mr-2"
                        />
                        WhatsApp
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="both"
                          checked={message.send_via === 'both'}
                          onChange={(e) => setMessage({...message, send_via: e.target.value})}
                          className="mr-2"
                        />
                        Both
                      </label>
                    </div>
                  </div>

                  <Button
                    onClick={sendMessage}
                    disabled={loading}
                    className="w-full"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardEnhanced;
